/**
 * Bidirectional mapper between friendly URL params and Elasticsearch facet queries.
 *
 * URL (user-friendly):  ?calificacion=1&marca=bosch&material=acero
 * Elastic (API payload): facet=x_ratings.rating:([1 TO 5])
 *                        facet=manufacturer.raw:"BOSCH"
 *                        facet=facets.10100.value.key:"Acero"
 *
 * Migrated from React store (use-product-filter.tsx). The friendly URL key is
 * derived from the facet's `name` field (slugified) — so any new facet type
 * coming from the API works out of the box. Only special-case behaviors are
 * hardcoded (rating, brand, category, etc.).
 */

import { ProductFacet, ProductFacetEntry } from '@/data/types/Product';

// ---------------------------------------------------------------------------
// Slug helpers (ported from React store)
// ---------------------------------------------------------------------------

const stripAccents = (str: string): string => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Generic URL slug: lowercase + strip accents + spaces → dashes */
export const transformToUrlSlug = (str: string): string =>
	stripAccents(str).toLowerCase().replace(/\s+/g, '-');

/** Brand slug: stricter cleanup (special chars, & → and, +) */
export const transformBrandSlug = (str: string): string => {
	let result = String(str).toLowerCase();
	result = result.replace(/[‘';.,_¿?=#%½|¡!$()[\]{}*"<>ª,º]/gi, '');
	result = result.replaceAll('@', '-');
	result = result.replaceAll('&', 'and').replaceAll('+', 'and');
	result = stripAccents(result);
	result = result.replace(/\s+/g, '-').replace(/-{2,}/g, '-');
	return result;
};

// ---------------------------------------------------------------------------
// Special facet identifiers (match against facet.name / facet.value)
// ---------------------------------------------------------------------------

const RATING_FIELD = 'x_ratings.rating';
const RATING_URL_KEY = transformToUrlSlug('Calificación');
const CATEGORY_NAME = 'Categoría';
const BRAND_NAME = 'Marca';

// ---------------------------------------------------------------------------
// Reserved (non-facet) URL keys — anything else in the URL is treated as a
// friendly facet param. Keeping this list small means new facet types from
// the API work without code changes here.
// ---------------------------------------------------------------------------

export const SORT_URL_KEY = 'ordenarpor';

export const NON_FACET_URL_KEYS = new Set<string>([
	'path',
	'orderBy',
	SORT_URL_KEY,
	'minPrice',
	'maxPrice',
	'facet',
	'facetLimit',
	'offset',
	'limit',
	'physicalStoreId',
	'stLocId',
	'marketId',
	'searchTerm',
	'sameDay',
]);

export const isFacetUrlKey = (key: string): boolean => !NON_FACET_URL_KEYS.has(key);

// ---------------------------------------------------------------------------
// Sort (orderBy) mapping — URL uses friendly slug (e.g. "nombre"); API expects
// the numeric orderBy string (e.g. "2"). Values match the React store.
// ---------------------------------------------------------------------------

const ORDER_BY_TO_SORT_SLUG: Record<string, string> = {
	'0': 'relevancia',
	'1': 'marca',
	'2': 'nombre',
	'3': 'preciomasbajo',
	'4': 'preciomasalto',
	'5': 'masvendidos',
};

const SORT_SLUG_TO_ORDER_BY: Record<string, string> = Object.entries(ORDER_BY_TO_SORT_SLUG).reduce(
	(acc, [orderBy, slug]) => ({ ...acc, [slug]: orderBy }),
	{} as Record<string, string>
);

export const orderByToSortSlug = (orderBy: string): string | undefined =>
	ORDER_BY_TO_SORT_SLUG[orderBy];

export const sortSlugToOrderBy = (slug: string): string | undefined => SORT_SLUG_TO_ORDER_BY[slug];

/** Decode a value that may use both %XX encoding and `+` for space (form-encoded). */
const safeDecode = (str: string): string => {
	try {
		return decodeURIComponent(str.replaceAll('+', ' '));
	} catch {
		return str.replaceAll('+', ' ');
	}
};

// ---------------------------------------------------------------------------
// URL → Elastic
// ---------------------------------------------------------------------------

/** Rating conversion: uses metadata entry.value when available so encoding matches the API. */
const resolveRatingFacet = (urlValue: string, facets?: ProductFacet[]): string | null => {
	const stars = parseInt(urlValue, 10);
	if (!stars || stars < 1 || stars > 5) return null;
	const label = `([${stars} TO 5])`;
	if (facets?.length) {
		const ratingFacet = facets.find((f) => f.value.includes(RATING_FIELD));
		const entry = ratingFacet?.entry.find((e) => e.label === label);
		if (entry) return entry.value;
	}
	return `${RATING_FIELD}:${label}`;
};

const CATEGORY_PREFIX = 'path.tree:';

/** Ensure a category entry.value has the `path.tree:` prefix (handles both raw and mapped facets). */
const ensureCategoryPrefix = (value: string): string =>
	value.startsWith(CATEGORY_PREFIX) ? value : `${CATEGORY_PREFIX}${value}`;

/** Generic facet conversion: looks up the entry by slugified label match. */
const resolveGenericFacet = (
	urlKey: string,
	urlValue: string,
	facets: ProductFacet[]
): string | null => {
	const facet = facets.find((f) => transformToUrlSlug(f.name) === urlKey);
	if (!facet) return null;
	const slugify = facet.name === BRAND_NAME ? transformBrandSlug : transformToUrlSlug;
	const entry = facet.entry.find((e) => slugify(e.label) === urlValue);
	if (!entry) return null;
	return facet.name === CATEGORY_NAME ? ensureCategoryPrefix(entry.value) : entry.value;
};

/**
 * Convert one friendly URL param (key + value) to its elastic facet query.
 * Uses the facets metadata to look up the matching entry; rating is the only
 * fully deterministic case that works without metadata.
 */
export const friendlyUrlToElasticFacet = (
	urlKey: string,
	urlValue: string,
	facets?: ProductFacet[]
): string | null => {
	if (urlKey === RATING_URL_KEY) return resolveRatingFacet(urlValue, facets);
	if (!facets?.length) return null;
	return resolveGenericFacet(urlKey, urlValue, facets);
};

/**
 * Convert all friendly facet params from a query object to an array of
 * elastic facet queries. Any URL key not in NON_FACET_URL_KEYS is considered
 * a candidate facet param.
 */
export const friendlyQueryToElasticFacets = (
	query: Record<string, string | string[] | undefined>,
	facets?: ProductFacet[]
): string[] =>
	Object.entries(query)
		.filter(([urlKey, raw]) => raw !== undefined && isFacetUrlKey(urlKey))
		.flatMap(([urlKey, raw]) => {
			const values = Array.isArray(raw) ? raw : [raw as string];
			return values
				.map((value) => friendlyUrlToElasticFacet(urlKey, String(value), facets))
				.filter((v): v is string => v !== null);
		});

// ---------------------------------------------------------------------------
// Elastic → URL
// ---------------------------------------------------------------------------

export const extractElasticField = (facetValue: string): string => {
	const decoded = safeDecode(facetValue);
	const colon = decoded.indexOf(':');
	return colon > -1 ? decoded.slice(0, colon) : decoded;
};

/** Try to map a single (facet, entry) pair against an elastic value */
const matchEntry = (
	facet: ProductFacet,
	entry: ProductFacetEntry,
	decoded: string
): { key: string; value: string } | null => {
	const isCategory = facet.name === CATEGORY_NAME;
	const isBrand = facet.name === BRAND_NAME;
	const slugify = isBrand ? transformBrandSlug : transformToUrlSlug;
	// Normalize both sides: entry.value may or may not have `path.tree:` depending on raw vs mapped facets
	const entryElastic = isCategory ? ensureCategoryPrefix(entry.value) : entry.value;
	const normalizedDecoded = extractElasticField(decoded).replace('.', '');
	const facetName = entry.extendedData.name?.toLowerCase() ?? '';
	const matches = safeDecode(entryElastic) === decoded || normalizedDecoded === facetName;
	return matches ? { key: transformToUrlSlug(facet.name), value: slugify(entry.label) } : null;
};

/**
 * Convert an elastic facet query to its friendly URL pair. Walks the facets
 * metadata to find the parent facet (by entry.value match), then derives the
 * URL key/value following the React project's rules.
 */
export const elasticFacetToFriendlyUrl = (
	facetValue: string,
	facets?: ProductFacet[]
): { key: string; value: string } | null => {
	const decoded = safeDecode(facetValue);
	const field = extractElasticField(decoded);

	// Rating: deterministic — no metadata needed
	if (field === RATING_FIELD) {
		const match = decoded.match(/\(\[(\d) TO 5\]\)/);
		return match ? { key: RATING_URL_KEY, value: match[1] } : null;
	}

	if (!facets?.length) return null;

	return (
		facets
			.flatMap((facet) => facet.entry.map((entry) => matchEntry(facet, entry, decoded)))
			.find((m): m is { key: string; value: string } => m !== null) ?? null
	);
};
